import cn from 'clsx';
import { makeStyles, DefaultTheme } from '@material-ui/styles';
import { useMediaQuery } from '@material-ui/core';

import OverflowTooltip from '../../OverflowTooltip';

type UserJobDetailsProps = {
    role?: string;
    company?: string;
    web?: string;
    clamp?: number | 'none' | 'inherit';
    className?: string;
    variant?: keyof DefaultTheme['typography'];
    disablePortals?: boolean;
    style?: React.CSSProperties;
};

const AT_SIGN = ` @\u00a0`;

const getClickableLink = (link: string): string =>
    link.startsWith('http://') || link.startsWith('https://') ? link : `//${link}`;

const UserJobDetails: React.FC<UserJobDetailsProps> = ({
    role = '',
    company = '',
    web = '',
    className,
    clamp = 1,
    variant = 't71',
    disablePortals = false,
    style = {},
}) => {
    const classes = useStyles();
    const classNames = cn(classes.root, classes[variant], clamp && classes.clamp, className);
    const withHover = useMediaQuery('@media(hover: hover) and (pointer: fine)');

    if (!role && !company) {
        return null;
    }

    const jobDetails = (
        <>
            {role}
            <CompanyAsTextOrLink company={company} web={web} className={classes.inheritLink} />
        </>
    );

    if (withHover) {
        return (
            <OverflowTooltip content={jobDetails} disablePortals={disablePortals}>
                {clampedElRef => (
                    <span
                        style={{ WebkitLineClamp: clamp ? clamp : 'inherit', ...style }}
                        className={classNames}
                        ref={clampedElRef}
                    >
                        {jobDetails}
                    </span>
                )}
            </OverflowTooltip>
        );
    }

    return <span className={classNames}>{jobDetails}</span>;
};

const CompanyAsTextOrLink = ({
    company = '',
    web = '',
    className,
}: {
    company: string;
    web: string;
    className?: string;
}) => {
    if (!company) {
        return null;
    }

    if (!web) {
        return (
            <>
                {AT_SIGN}
                {company}
            </>
        );
    }

    return (
        <>
            {AT_SIGN}
            <a
                href={getClickableLink(web)}
                className={className}
                target="_blank"
                rel="noopener noreferrer"
            >
                {company}
            </a>
        </>
    );
};

const useStyles = makeStyles(
    theme => ({
        root: {
            color: theme.palette.greys[500],
            maxWidth: '100%',
            wordBreak: 'break-word',
            lineHeight: '132%',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            display: 'inline-block',
        },
        t10: { ...theme.typography.t10 },
        t15: { ...theme.typography.t15 },
        t20: { ...theme.typography.t20 },
        t30: { ...theme.typography.t30 },
        t31: { ...theme.typography.t31 },
        t40: { ...theme.typography.t40 },
        t50: { ...theme.typography.t50 },
        t60: { ...theme.typography.t60 },
        t61: { ...theme.typography.t61 },
        t70: { ...theme.typography.t70 },
        t71: { ...theme.typography.t71 },
        t80: { ...theme.typography.t80 },
        t81: { ...theme.typography.t81 },
        clamp: {
            display: '-webkit-box',
            '-webkit-box-orient': 'vertical',
        },
        inheritLink: {
            color: 'inherit',
            textUnderlineOffset: '1.5px',
            textDecorationColor: 'transparent',
            '-webkit-text-decoration-color': 'transparent',
            '-moz-text-decoration-color': 'transparent',
            transition: 'all 300ms ease-in-out',

            '&:hover, &:active': {
                color: theme.palette.greys[700],
                textDecorationColor: theme.palette.greys[700],
                '-webkit-text-decoration-color': theme.palette.greys[700],
                '-moz-text-decoration-color': theme.palette.greys[700],
            },
        },
    }),
    { classNamePrefix: 'user-job-title' }
);

export default UserJobDetails;
